import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AxiosApiCall from "../api/AxiosApiCall";
import ApiServices from "../api/ApiServices";
import { toast } from "react-toastify";

const axiosClient = AxiosApiCall();
const STALE_5MIN = 1000 * 60 * 5;
const STALE_10MIN = 1000 * 60 * 10;
const STALE_30MIN = 1000 * 60 * 30;

// ========================= CATEGORIES =========================
export const useCategories = () =>
  useQuery({
    queryKey: ["categories"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getCategory); return r.data; },
    staleTime: STALE_10MIN,
  });

export const useDeleteCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (categoryId) => {
      const r = await axiosClient.delete(ApiServices.removeCategory, { data: { category_id: categoryId } });
      return r.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["categories"] }); toast.success("Category deleted!"); },
    onError: (e) => toast.error(e?.response?.data?.message || e?.message || "Failed to delete category"),
  });
};

// ========================= SUB CATEGORIES =========================
export const useSubCategories = (categoryId) =>
  useQuery({
    queryKey: ["subCategories", categoryId],
    queryFn: async () => {
      const params = categoryId ? `?category_id=${categoryId}` : "";
      const r = await axiosClient.get(`${ApiServices.getSubCategoryById}${params}`);
      return r.data;
    },
    staleTime: STALE_10MIN,
  });

export const useDeleteSubCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (subCategoryId) => {
      const r = await axiosClient.delete(ApiServices.removeSubCategory, { data: { sub_category_id: subCategoryId } });
      return r.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["subCategories"] }); toast.success("Sub-category deleted!"); },
    onError: (e) => toast.error(e?.response?.data?.message || e?.message || "Failed to delete sub-category"),
  });
};

// ========================= STORES =========================
export const useStores = () =>
  useQuery({
    queryKey: ["stores"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getAllStore); return r.data; },
    staleTime: STALE_10MIN,
  });

export const useDeleteStore = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (storeId) => {
      const r = await axiosClient.delete(ApiServices.removeStore, { data: { store_id: storeId } });
      return r.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["stores"] }); toast.success("Store deleted!"); },
    onError: (e) => toast.error(e?.response?.data?.message || e?.message || "Failed to delete store"),
  });
};

// ========================= VENDORS =========================
export const useVendors = (filters = {}) => {
  const { search = "", status = "", start_date = "", end_date = "" } = filters;
  return useQuery({
    queryKey: ["vendors", search, status, start_date, end_date],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (status) params.append("status", status);
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      const url = `${ApiServices.getAllVendor}${params.toString() ? `?${params}` : ""}`;
      const r = await axiosClient.get(url);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= FLASH SALES =========================
export const useFlashSales = () =>
  useQuery({
    queryKey: ["flashSales"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getAllFlashsales); return r.data; },
    staleTime: STALE_5MIN,
  });

export const useDeleteFlashSaleItem = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (flashSaleId) => {
      const r = await axiosClient.delete(ApiServices.removeFlashsales, { data: { flash_sale_id: flashSaleId } });
      return r.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["flashSales"] }); toast.success("Flash sale deleted!"); },
    onError: (e) => toast.error(e?.response?.data?.message || e?.message || "Failed to delete flash sale"),
  });
};

// ========================= ORDERS =========================
export const useOrders = (filters = {}) => {
  const { search = "", status = "", start_date = "", end_date = "", page = "", per_page = "" } = filters;
  return useQuery({
    queryKey: ["orders", search, status, start_date, end_date, page, per_page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (status) params.append("status", status);
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      if (page) params.append("page", page);
      if (per_page) params.append("per_page", per_page);
      const url = `${ApiServices.getAllOrders}${params.toString() ? `?${params}` : ""}`;
      const r = await axiosClient.get(url);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= DASHBOARD =========================
export const useDashboard = () =>
  useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getDashboard); return r.data; },
    staleTime: STALE_5MIN,
  });

// ========================= SLIDERS =========================
export const useSliders = () =>
  useQuery({
    queryKey: ["sliders"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.AllSlider); return r.data; },
    staleTime: STALE_10MIN,
  });

export const useDeleteSlider = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sliderId) => {
      const r = await axiosClient.delete(ApiServices.deleteSlider, { data: { slider_id: sliderId } });
      return r.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["sliders"] }); toast.success("Slider deleted!"); },
    onError: (e) => toast.error(e?.response?.data?.message || e?.message || "Failed to delete slider"),
  });
};

// ========================= APP SETTINGS =========================
export const useAppSettings = () =>
  useQuery({
    queryKey: ["appSettings"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getAllAppSettings); return r.data; },
    staleTime: STALE_10MIN,
  });

// ========================= RIDERS =========================
export const useRiders = (filters = {}) => {
  const { search = "", status = "", start_date = "", end_date = "" } = filters;
  return useQuery({
    queryKey: ["riders", search, status, start_date, end_date],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (status) params.append("status", status);
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      const url = `${ApiServices.getAllRiders}${params.toString() ? `?${params}` : ""}`;
      const r = await axiosClient.get(url);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= CLUSTER LOCATIONS =========================
export const useClusterLocations = () =>
  useQuery({
    queryKey: ["clusterLocations"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getClusterLocation); return r.data; },
    staleTime: STALE_10MIN,
  });

export const useDeleteClusterLocation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (locationId) => {
      const r = await axiosClient.delete(ApiServices.deleteClusterLocation, { data: { address_id: locationId } });
      return r.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clusterLocations"] }); toast.success("Location deleted!"); },
    onError: (e) => toast.error(e?.response?.data?.message || e?.message || "Failed to delete location"),
  });
};

// ========================= PENDING PRODUCT STOCK =========================
export const usePendingProductStock = () =>
  useQuery({
    queryKey: ["pendingProductStock"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.pendingProductStock); return r.data; },
    staleTime: STALE_5MIN,
  });

// ========================= PHYSICAL PRODUCTS =========================
export const usePhysicalProducts = () =>
  useQuery({
    queryKey: ["physicalProducts"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getAllPhysicalProduct); return r.data; },
    staleTime: STALE_5MIN,
  });

// ========================= PRODUCT PROPERTY =========================
export const useProductProperties = () =>
  useQuery({
    queryKey: ["productProperties"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getProductProperty); return r.data; },
    staleTime: STALE_10MIN,
  });

// ========================= SECURITY QUESTIONS =========================
export const useSecurityQuestions = () =>
  useQuery({
    queryKey: ["securityQuestions"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getAllSecurityQuestion); return r.data; },
    staleTime: STALE_10MIN,
  });

export const useSecurityQuestionStats = () =>
  useQuery({
    queryKey: ["securityQuestionStats"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getSecurityQuestionStats); return r.data; },
    staleTime: STALE_5MIN,
  });

export const useDeleteSecurityQuestion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (questionId) => {
      const r = await axiosClient.delete(ApiServices.deleteSecurityQuestion, { data: { question_id: questionId } });
      return r.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["securityQuestions"] }); toast.success("Question deleted!"); },
    onError: (e) => toast.error(e?.response?.data?.message || e?.message || "Failed to delete question"),
  });
};

// ========================= TICKETS =========================
export const useTickets = () =>
  useQuery({
    queryKey: ["tickets"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getAllTicket); return r.data; },
    staleTime: STALE_5MIN,
  });

// ========================= DELIVERY PRICES =========================
export const useDeliveryPrices = () =>
  useQuery({
    queryKey: ["deliveryPrices"],
    queryFn: async () => { const r = await axiosClient.post(ApiServices.getAllDeliveryLocprice, {}); return r.data; },
    staleTime: STALE_10MIN,
  });

export const useDeleteDeliveryPrice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (priceId) => {
      const r = await axiosClient.delete(ApiServices.deleteDeliveryLocprice, { data: { price_id: priceId } });
      return r.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["deliveryPrices"] }); toast.success("Price deleted!"); },
    onError: (e) => toast.error(e?.response?.data?.message || e?.message || "Failed to delete price"),
  });
};

// ========================= COMMISSIONS =========================
export const useCommissions = () =>
  useQuery({
    queryKey: ["commissions"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getAllPhysicalProductCom); return r.data; },
    staleTime: STALE_10MIN,
  });

export const useDeleteCommission = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (commissionId) => {
      const r = await axiosClient.delete(ApiServices.deletePhysicalProductCom, { data: { commission_id: commissionId } });
      return r.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["commissions"] }); toast.success("Commission deleted!"); },
    onError: (e) => toast.error(e?.response?.data?.message || e?.message || "Failed to delete commission"),
  });
};

// ========================= DENOMINATIONS =========================
export const useDenominations = () =>
  useQuery({
    queryKey: ["denominations"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getDenominations); return r.data; },
    staleTime: STALE_10MIN,
  });

export const useDeleteDenomination = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (denominationId) => {
      const r = await axiosClient.delete(ApiServices.deleteDenomination, { data: { denomination_id: denominationId } });
      return r.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["denominations"] }); toast.success("Denomination deleted!"); },
    onError: (e) => toast.error(e?.response?.data?.message || e?.message || "Failed to delete denomination"),
  });
};

// ========================= BILLERS =========================
export const useBillers = () =>
  useQuery({
    queryKey: ["billers"],
    queryFn: async () => { const r = await axiosClient.post(ApiServices.getBillers, {}); return r.data; },
    staleTime: STALE_10MIN,
  });

export const useBillsProducts = () =>
  useQuery({
    queryKey: ["billsProducts"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getBillsProduct); return r.data; },
    staleTime: STALE_10MIN,
  });

export const useSwitchers = () =>
  useQuery({
    queryKey: ["switchers"],
    queryFn: async () => { const r = await axiosClient.post(ApiServices.getSwitchers, {}); return r.data; },
    staleTime: STALE_10MIN,
  });

// ========================= RETURNED ORDERS =========================
export const useReturnedOrders = (filters = {}) => {
  const { customer_id = "" } = filters;
  return useQuery({
    queryKey: ["returnedOrders", customer_id],
    queryFn: async () => {
      const params = customer_id ? `?customer_id=${customer_id}` : "";
      const r = await axiosClient.get(`${ApiServices.getReturnedOrdersByCustomer}${params}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= WITHDRAWAL REQUESTS =========================
export const useWithdrawalRequests = () =>
  useQuery({
    queryKey: ["withdrawalRequests"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getVendorWithrawalRequest); return r.data; },
    staleTime: STALE_5MIN,
  });

// ========================= BRANCHES =========================
export const useBranches = () =>
  useQuery({
    queryKey: ["branches"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getAllBranchOperations); return r.data; },
    staleTime: STALE_5MIN,
  });

export const useBranchDashboard = (filters = {}) => {
  const { period = "this_month", start_date = "", end_date = "" } = filters;
  return useQuery({
    queryKey: ["branchDashboard", period, start_date, end_date],
    queryFn: async () => {
      const body = {};
      if (period) body.period = period;
      if (start_date) body.start_date = start_date;
      if (end_date) body.end_date = end_date;
      const r = await axiosClient.post(ApiServices.getBranchDashboard, body);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const useBranchActivityLog = (branchId) =>
  useQuery({
    queryKey: ["branchActivityLog", branchId],
    queryFn: async () => {
      const r = await axiosClient.get(`${ApiServices.getBranchOpeActivity}?branch_id=${branchId}`);
      return r.data;
    },
    enabled: !!branchId,
    staleTime: STALE_5MIN,
  });

// ========================= BRANCH CUSTOMERS =========================
export const useBranchCustomers = (filters = {}) => {
  const { search = "", branch_id = "" } = filters;
  return useQuery({
    queryKey: ["branchCustomers", search, branch_id],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (branch_id) params.append("branch_id", branch_id);
      const url = `${ApiServices.getBranchCustomer}${params.toString() ? `?${params}` : ""}`;
      const r = await axiosClient.get(url);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= BRANCH PRODUCTS =========================
export const useBranchProducts = (branchId) =>
  useQuery({
    queryKey: ["branchProducts", branchId],
    queryFn: async () => {
      const url = branchId ? `${ApiServices.getAllBranchProduct}?branch_id=${branchId}` : ApiServices.getAllBranchProduct;
      const r = await axiosClient.get(url);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });

export const useBranchProductCategories = (branchId) =>
  useQuery({
    queryKey: ["branchProductCategories", branchId],
    queryFn: async () => {
      const body = branchId ? { branch_id: branchId } : {};
      const r = await axiosClient.post(ApiServices.getAllBranchProductCategory, body);
      return r.data;
    },
    staleTime: STALE_10MIN,
  });

export const useBranchProductUnits = (branchId) =>
  useQuery({
    queryKey: ["branchProductUnitsOps", branchId],
    queryFn: async () => {
      const url = branchId ? `${ApiServices.getAllBranchProductUnit}?branch_id=${branchId}` : ApiServices.getAllBranchProductUnit;
      const r = await axiosClient.get(url);
      return r.data;
    },
    staleTime: STALE_10MIN,
  });

// ========================= BRANCH SALES HISTORY =========================
export const useBranchSalesHistory = (filters = {}) => {
  const { branch_id = "", start_date = "", end_date = "", per_page = "", page = "" } = filters;
  return useQuery({
    queryKey: ["branchSalesHistory", branch_id, start_date, end_date, per_page, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branch_id) params.append("branch_id", branch_id);
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      if (per_page) params.append("per_page", per_page);
      if (page) params.append("page", page);
      const r = await axiosClient.get(`${ApiServices.getBranchSalesHistory}${params.toString() ? `?${params}` : ""}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= REPORTS =========================
export const useTopBranchReport = (filters = {}) => {
  const { period = "this_month", metric = "transactions", limit = 10, start_date = "", end_date = "" } = filters;
  return useQuery({
    queryKey: ["topBranchReport", period, metric, limit, start_date, end_date],
    queryFn: async () => {
      const body = {};
      if (period) body.period = period;
      if (metric) body.metric = metric;
      if (limit) body.limit = limit;
      if (start_date) body.start_date = start_date;
      if (end_date) body.end_date = end_date;
      const r = await axiosClient.post(ApiServices.getTopBranchReport, body);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const useTopBranchCustomerReport = (filters = {}) => {
  const { branch_id = "", start_date = "", end_date = "" } = filters;
  return useQuery({
    queryKey: ["topBranchCustomerReport", branch_id, start_date, end_date],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branch_id) params.append("branch_id", branch_id);
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      const r = await axiosClient.get(`${ApiServices.getTopBranchCustomerReport}${params.toString() ? `?${params}` : ""}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= WALLET HISTORY =========================
export const useWalletHistory = (filters = {}) => {
  const { branch_id = "", start_date = "", end_date = "", per_page = "", page = "" } = filters;
  return useQuery({
    queryKey: ["walletHistory", branch_id, start_date, end_date, per_page, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branch_id) params.append("branch_id", branch_id);
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      if (per_page) params.append("per_page", per_page);
      if (page) params.append("page", page);
      const r = await axiosClient.get(`${ApiServices.getAllWalletHistory}${params.toString() ? `?${params}` : ""}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const useTransactionHistory = (filters = {}) => {
  const { branch_id = "", start_date = "", end_date = "", per_page = "", page = "" } = filters;
  return useQuery({
    queryKey: ["transactionHistory", branch_id, start_date, end_date, per_page, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branch_id) params.append("branch_id", branch_id);
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      if (per_page) params.append("per_page", per_page);
      if (page) params.append("page", page);
      const r = await axiosClient.get(`${ApiServices.getTransactionHistory}${params.toString() ? `?${params}` : ""}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= PURCHASES =========================
export const useAllPurchases = (filters = {}) => {
  const { branch_id = "", per_page = "", page = "" } = filters;
  return useQuery({
    queryKey: ["allPurchases", branch_id, per_page, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branch_id) params.append("branch_id", branch_id);
      if (per_page) params.append("per_page", per_page);
      if (page) params.append("page", page);
      const r = await axiosClient.get(`${ApiServices.getAllPurchaseItem}${params.toString() ? `?${params}` : ""}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const usePendingPurchases = (filters = {}) => {
  const { branch_id = "", per_page = "", page = "" } = filters;
  return useQuery({
    queryKey: ["pendingPurchases", branch_id, per_page, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branch_id) params.append("branch_id", branch_id);
      if (per_page) params.append("per_page", per_page);
      if (page) params.append("page", page);
      const r = await axiosClient.get(`${ApiServices.getPendingPurchaseItem}${params.toString() ? `?${params}` : ""}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= REMITTANCES =========================
export const useAllRemittances = (filters = {}) => {
  const { branch_id = "", per_page = "", page = "" } = filters;
  return useQuery({
    queryKey: ["allRemittances", branch_id, per_page, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branch_id) params.append("branch_id", branch_id);
      if (per_page) params.append("per_page", per_page);
      if (page) params.append("page", page);
      const r = await axiosClient.get(`${ApiServices.getAllRemittance}${params.toString() ? `?${params}` : ""}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const usePendingRemittances = (filters = {}) => {
  const { branch_id = "", per_page = "", page = "" } = filters;
  return useQuery({
    queryKey: ["pendingRemittances", branch_id, per_page, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branch_id) params.append("branch_id", branch_id);
      if (per_page) params.append("per_page", per_page);
      if (page) params.append("page", page);
      const r = await axiosClient.get(`${ApiServices.getPendingRemittance}${params.toString() ? `?${params}` : ""}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const useProcessedRemittances = (filters = {}) => {
  const { branch_id = "", per_page = "", page = "" } = filters;
  return useQuery({
    queryKey: ["processedRemittances", branch_id, per_page, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branch_id) params.append("branch_id", branch_id);
      if (per_page) params.append("per_page", per_page);
      if (page) params.append("page", page);
      const r = await axiosClient.get(`${ApiServices.acceptProcessRemittance}${params.toString() ? `?${params}` : ""}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= EXPENSES =========================
export const useExpenses = (filters = {}) => {
  const { branch_id = "", per_page = "", page = "" } = filters;
  return useQuery({
    queryKey: ["expenses", branch_id, per_page, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branch_id) params.append("branch_id", branch_id);
      if (per_page) params.append("per_page", per_page);
      if (page) params.append("page", page);
      const r = await axiosClient.get(`${ApiServices.getExpenseOpePending}${params.toString() ? `?${params}` : ""}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= INCENTIVES =========================
export const usePendingIncentives = (filters = {}) => {
  const { branch_id = "" } = filters;
  return useQuery({
    queryKey: ["pendingIncentives", branch_id],
    queryFn: async () => {
      const url = branch_id ? `${ApiServices.pendingBranchOpeCreateIncentives}?branch_id=${branch_id}` : ApiServices.pendingBranchOpeCreateIncentives;
      const r = await axiosClient.get(url);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const useIncentiveStats = (branchId) =>
  useQuery({
    queryKey: ["incentiveStats", branchId],
    queryFn: async () => {
      const r = await axiosClient.get(`${ApiServices.getBranchOpeStatistic}${branchId ? `?branch_id=${branchId}` : ""}`);
      return r.data;
    },
    enabled: !!branchId,
    staleTime: STALE_5MIN,
  });

// ========================= STOCK MONITORING =========================
export const useBranchStockMonitoring = (filters = {}) => {
  const { branch_id = "", product_id = "", start_date = "", end_date = "", per_page = 20, page = 1, search = "" } = filters;
  return useQuery({
    queryKey: ["branchStockMonitoring", branch_id, product_id, start_date, end_date, per_page, page, search],
    queryFn: async () => {
      const body = {};
      if (branch_id) body.branch_id = branch_id;
      if (product_id) body.product_id = product_id;
      if (start_date) body.start_date = start_date;
      if (end_date) body.end_date = end_date;
      if (per_page) body.per_page = per_page;
      if (page) body.page = page;
      if (search) body.search = search;
      const r = await axiosClient.post(ApiServices.branchStockMonitoring, body);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= PRICE LIST =========================
export const usePriceList = (filters = {}) => {
  const { branch_id = "" } = filters;
  return useQuery({
    queryKey: ["priceList", branch_id],
    queryFn: async () => {
      const url = branch_id ? `${ApiServices.getProducts}?branch_id=${branch_id}` : ApiServices.getProducts;
      const r = await axiosClient.get(url);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= PRODUCT CATEGORY LIST =========================
export const useProductCategoryList = (filters = {}) => {
  const { sub_category_id = "" } = filters;
  return useQuery({
    queryKey: ["productCategoryList", sub_category_id],
    queryFn: async () => {
      const url = sub_category_id ? `${ApiServices.getProductsBySubCategoryId}?sub_category_id=${sub_category_id}` : ApiServices.getProductsBySubCategoryId;
      const r = await axiosClient.get(url);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= VENDOR DETAILS =========================
export const useVendorById = (vendorId) =>
  useQuery({
    queryKey: ["vendor", vendorId],
    queryFn: async () => { const r = await axiosClient.get(`${ApiServices.getVendorById}?vendor_id=${vendorId}`); return r.data; },
    enabled: !!vendorId,
    staleTime: STALE_5MIN,
  });

export const useVendorBalance = (vendorId) =>
  useQuery({
    queryKey: ["vendorBalance", vendorId],
    queryFn: async () => { const r = await axiosClient.get(`${ApiServices.getVendorBalance}?vendor_id=${vendorId}`); return r.data; },
    enabled: !!vendorId,
    staleTime: STALE_5MIN,
  });

export const useVendorTransactionHistory = (vendorId, filters = {}) => {
  const { start_date = "", end_date = "" } = filters;
  return useQuery({
    queryKey: ["vendorTransactions", vendorId, start_date, end_date],
    queryFn: async () => {
      const params = new URLSearchParams({ vendor_id: vendorId });
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      const r = await axiosClient.get(`${ApiServices.getVendorTransactionHistory}?${params}`);
      return r.data;
    },
    enabled: !!vendorId,
    staleTime: STALE_5MIN,
  });
};

// ========================= BRANCH INVENTORY REQUESTS =========================
export const useBranchInventoryRequests = () =>
  useQuery({
    queryKey: ["branchInventoryRequests"],
    queryFn: async () => { const r = await axiosClient.get(ApiServices.getBranchInventoryRequests); return r.data; },
    staleTime: STALE_5MIN,
  });

export const useBranchInventoryRequestHistory = (filters = {}) => {
  const { branch_id = "", head_office_id = "", start_date = "", end_date = "", per_page = 15, page = 1 } = filters;
  return useQuery({
    queryKey: ["branchInventoryRequestHistory", branch_id, head_office_id, start_date, end_date, per_page, page],
    queryFn: async () => {
      const params = new URLSearchParams({ per_page, page });
      if (branch_id) params.append("branch_id", branch_id);
      if (head_office_id) params.append("head_office_id", head_office_id);
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      const r = await axiosClient.get(`${ApiServices.getBranchInventoryRequestHistory}?${params}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const useHeadOfficePendingDeliveries = (filters = {}) => {
  const { page = 1, per_page = 20 } = filters;
  return useQuery({
    queryKey: ["headOfficePendingDeliveries", page, per_page],
    queryFn: async () => {
      const params = new URLSearchParams({ page, per_page });
      const r = await axiosClient.get(`${ApiServices.getHeadOfficePendingDeliveries}?${params}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= ADMIN ACTIVITY LOG =========================
export const useAdminActLog = (adminId, filters = {}) => {
  const { start_date = "", end_date = "" } = filters;
  return useQuery({
    queryKey: ["adminActLog", adminId, start_date, end_date],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (adminId) params.append("admin_id", adminId);
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      const r = await axiosClient.get(`${ApiServices.getAdminActivityLog}${params.toString() ? `?${params}` : ""}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= ADMIN ACTIVITY LOG =========================
export const useAdminActivityLog = (filters = {}) => {
  const { page = 1, per_page = 1000, search = "", email = "", start_date = "", end_date = "" } = filters;
  return useQuery({
    queryKey: ["adminActivityLog", page, per_page, search, email, start_date, end_date],
    queryFn: async () => {
      const params = new URLSearchParams({ page, per_page, sort_by: "created_at", sort_order: "desc" });
      if (search) params.append("search", search);
      if (email) params.append("email", email);
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      const r = await axiosClient.get(`${ApiServices.getAdminActivityLog}?${params}`);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= ADMINS =========================
export const useAdmins = () =>
  useQuery({
    queryKey: ["admins"],
    queryFn: async () => {
      const r = await axiosClient.get(ApiServices.getAdmin);
      return r.data;
    },
    staleTime: STALE_5MIN,
  });

// ========================= PERMISSIONS =========================
export const usePermissions = () =>
  useQuery({
    queryKey: ["permissions"],
    queryFn: async () => {
      const r = await axiosClient.get(ApiServices.getPermissions);
      return r.data;
    },
    staleTime: STALE_10MIN,
  });

export const useRolePermissions = (roleId) =>
  useQuery({
    queryKey: ["rolePermissions", roleId],
    queryFn: async () => {
      const r = await axiosClient.get(ApiServices.getRolesPermission, { params: { role_id: roleId } });
      return r.data;
    },
    staleTime: STALE_5MIN,
    enabled: !!roleId,
  });

// ========================= BRANCH PRODUCT UNITS (paginated) =========================
export const useBranchProductUnitsPaginated = (filters = {}) => {
  const { per_page = 20, page = 1 } = filters;
  return useQuery({
    queryKey: ["branchProductUnitsPaginated", per_page, page],
    queryFn: async () => {
      const r = await axiosClient.post(ApiServices.getAllBranchProductUnit, { per_page, page });
      return r.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ========================= BILLS PRODUCTS FOR SWITCH =========================
export const useBillsProductsForSwitch = () =>
  useQuery({
    queryKey: ["billsProductsForSwitch"],
    queryFn: async () => {
      const r = await axiosClient.post(ApiServices.getBillsProductsForSwitch, {});
      return r.data;
    },
    staleTime: STALE_5MIN,
  });

// ========================= BILLS PRODUCTS BY TYPE =========================
export const useBillsProductsByType = (type) =>
  useQuery({
    queryKey: ["billsProductsByType", type],
    queryFn: async () => {
      const r = await axiosClient.post(ApiServices.getBillsProduct, { product: type });
      return r.data;
    },
    staleTime: STALE_5MIN,
    enabled: !!type,
  });
